import type { PaymentWallStatus } from "@cova/settings-core/contracts"
import { DEFAULT_PLATFORM_SCOPE, type BillingWallPolicy, type PlatformScope } from "@/domain/scope/platform-scope"

/**
 * WHERE THE PLATFORM GATE SENDS A FIRM THAT MUST PAY TO ENTER (spec
 * multi-product 023 G55 (2), G57 (3); 018 §8 P4b, P6, R-018-7; WP3.13.9c,
 * FIX-PLATFORM-SALE-GATE).
 *
 * - The broker (the default platform scope) is exactly today's gate: a firm
 *   admin is redirected to `/payment`, a member sees the "billing pending"
 *   page in place, decided by the request-time `requiresPayment`. Its grace
 *   periods and setup-due flow are decided before this (they clear
 *   `requiresPayment`) and are untouched.
 * - Any other scope whose DERIVED wall covers every surface
 *   (`billingWallPolicyOf(scope) === "all-surfaces"`, a billed port with no
 *   floor) is walled by the SERVER's answer, `getPaymentStatus`
 *   (`paymentStatus`), never by the raw derived wall: that status is already
 *   sale-gated (`saleGatedWallStatus`), so a firm still pre-checkout while its
 *   catalogue sells nothing reads `no-wall` and is let in, exactly as the Bots
 *   wall lets it in. A firm `pre-checkout` with a catalogue that sells, or
 *   `locked` after paying, sends admins AND members to `/payment`, where the
 *   shared PaymentWall shows checkout to the admin and "your firm is
 *   finishing setup" to everyone else. Unread (`null`) is not walled.
 * - A `paid-rungs` scope (underwriter: Free is the floor) is never routed
 *   here: its floor stays open and its own shell walls the paid rooms. Nor
 *   is a `none` scope. Both answer `none`.
 */
export type PaymentWallRoute = "redirect" | "pending-page" | "none"

export interface PaymentWallRouteFacts {
  /** The scope of the surface the person stands on (the scope the run is in). */
  readonly surfaceScope: PlatformScope
  /** That scope's derived wall (`billingWallPolicyOf`). */
  readonly wallPolicy: BillingWallPolicy
  /** The broker's request-time payment wall bites for this firm (no active subscription, not setup-due, not inactive). */
  readonly requiresPayment: boolean
  /** A non-broker firm's place at its wall from the server (`getPaymentStatus`, sale-gated); `null` while unread or not asked. */
  readonly paymentStatus: PaymentWallStatus | null
  readonly isAdmin: boolean
}

/** Whether the server's status keeps the firm out until it pays: pre-checkout with a catalogue that sells, or locked after paying. */
export const isPayToEnterWalled = (status: PaymentWallStatus | null): boolean =>
  status === "pre-checkout" || status === "locked"

export const paymentWallRouteOf = ({ surfaceScope, wallPolicy, requiresPayment, paymentStatus, isAdmin }: PaymentWallRouteFacts): PaymentWallRoute => {
  if (surfaceScope === DEFAULT_PLATFORM_SCOPE) {
    if (!requiresPayment) return "none"
    return isAdmin ? "redirect" : "pending-page"
  }
  return wallPolicy === "all-surfaces" && isPayToEnterWalled(paymentStatus) ? "redirect" : "none"
}
